import {
  ApplicationConfig,
  isDevMode,
  importProvidersFrom,
  APP_INITIALIZER,
  LOCALE_ID,
} from '@angular/core'
import {
  provideRouter,
  withPreloading,
  PreloadAllModules,
  withViewTransitions,
} from '@angular/router'
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http'
import { TranslateModule, TranslateLoader, TranslateService } from '@ngx-translate/core'
import { HttpClient } from '@angular/common/http'
import { Observable, firstValueFrom } from 'rxjs'

import { routes } from './app.routes'
import { provideServiceWorker } from '@angular/service-worker'
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async'

// Custom TranslateLoader class
export class CustomTranslateLoader implements TranslateLoader {
  constructor(private http: HttpClient) {}

  getTranslation(lang: string): Observable<any> {
    return this.http.get(`./assets/i18n/${lang}.json`)
  }
}

// Factory function for CustomTranslateLoader
export function HttpLoaderFactory(http: HttpClient) {
  return new CustomTranslateLoader(http)
}

/**
 * Preloads translations BEFORE the app bootstraps — eliminates the flash of
 * raw translation keys ("home.hero.title") visible in the few hundred ms
 * between render and translation download.
 *
 * Strategy:
 * - Pick persisted language (localStorage) or fallback to browser or 'fr'
 * - Wait for the JSON to be loaded before releasing the app
 * - Swallow errors so the app still boots if translations fail
 */
export function initTranslationsFactory(translate: TranslateService) {
  return async () => {
    const supported = ['fr', 'en']
    // Same storage key as PreferencesService ("sos-corp.language")
    const stored =
      typeof localStorage !== 'undefined'
        ? localStorage.getItem('sos-corp.language')
        : null
    const browser =
      typeof navigator !== 'undefined'
        ? (navigator.language || '').split('-')[0]
        : null
    const lang =
      (stored && supported.includes(stored) && stored) ||
      (browser && supported.includes(browser) && browser) ||
      'fr'

    translate.addLangs(supported)
    translate.setDefaultLang('fr')

    try {
      await firstValueFrom(translate.use(lang))
    } catch {
      // If translation download fails, fallback to default silently
      try {
        await firstValueFrom(translate.use('fr'))
      } catch {
        /* no-op */
      }
    }
  }
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(
      routes,
      withPreloading(PreloadAllModules),
      withViewTransitions(),
    ),
    provideHttpClient(withInterceptorsFromDi()),
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000',
    }),
    provideAnimationsAsync(),
    importProvidersFrom(
      TranslateModule.forRoot({
        defaultLanguage: 'fr',
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient],
        },
      }),
    ),
    // Preload translations before app renders
    {
      provide: APP_INITIALIZER,
      useFactory: initTranslationsFactory,
      deps: [TranslateService],
      multi: true,
    },
  ],
}
